import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Table,
  Switch,
  Typography,
  Popconfirm,
  Alert,
  message,
  Spin,
  Modal,
  Divider,
  Badge,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  ReloadOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';
import { webhookService } from '@services/admin/webhookService';

const { Text, Paragraph } = Typography;

const statusColors = {
  success: 'success',
  failed: 'error',
  pending: 'processing',
  retrying: 'warning',
};

const statusIcons = {
  success: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  pending: <ClockCircleOutlined />,
  retrying: <ExclamationCircleOutlined />,
};

export default function AdminWebhookShow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [webhook, setWebhook] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [fullSecret, setFullSecret] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    fetchWebhook();
  }, [id]);

  const fetchWebhook = async () => {
    try {
      setLoading(true);
      const data = await webhookService.get(id);
      if (data.success && data.data) {
        setWebhook(data.data);
      }
    } catch (error) {
      message.error(t('shared.webhooks.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const data = await webhookService.toggleActive(id);
      if (data.success) {
        message.success(t('shared.webhooks.messages.toggle_success'));
        fetchWebhook();
      }
    } catch (error) {
      message.error(t('shared.webhooks.error.update_failed'));
    }
  };

  const handleDelete = async () => {
    try {
      const data = await webhookService.delete(id);
      if (data.success) {
        message.success(t('shared.webhooks.messages.delete_success'));
        // Navigate back to integration if webhook belonged to one
        if (webhook?.integration_id) {
          navigate(`/integrations/${webhook.integration_id}`);
        } else {
          navigate('/webhooks');
        }
      }
    } catch (error) {
      message.error(t('shared.webhooks.error.delete_failed'));
    }
  };

  const handleTest = async () => {
    try {
      setTestLoading(true);
      const data = await webhookService.test(id);
      if (data.success) {
        const result = data.data;
        Modal.info({
          title: t('shared.webhooks.test_result.title'),
          content: (
            <Descriptions column={1} size="small" style={{ marginTop: 16 }}>
              <Descriptions.Item label={t('shared.webhooks.test_result.status')}>
                <Badge
                  status={result.status === 'success' ? 'success' : 'error'}
                  text={result.status}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.webhooks.test_result.response_code')}>
                {result.response_status || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.webhooks.test_result.duration')}>
                {result.duration_ms ? `${result.duration_ms}ms` : 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          ),
        });
        fetchWebhook();
      }
    } catch (error) {
      message.error(t('shared.webhooks.messages.test_failed'));
    } finally {
      setTestLoading(false);
    }
  };

  const handleRegenerateSecret = async () => {
    try {
      const data = await webhookService.regenerateSecret(id);
      if (data.success) {
        message.success(t('shared.webhooks.messages.regenerate_success'));
        setFullSecret(data.data.secret_key);
        setShowSecret(true);
        fetchWebhook();
      }
    } catch (error) {
      message.error(t('shared.webhooks.error.update_failed'));
    }
  };

  const handleCopySecret = () => {
    const secretToCopy = fullSecret || webhook?.secret_key;
    if (secretToCopy) {
      navigator.clipboard.writeText(secretToCopy);
      message.success(t('shared.webhooks.messages.copy_success'));
    }
  };

  const deliveryColumns = [
    {
      title: t('shared.webhooks.fields.events'),
      dataIndex: 'event_type',
      key: 'event_type',
      render: (text) => <Tag>{t(`shared.webhooks.events.${text}`, text)}</Tag>,
    },
    {
      title: t('shared.webhooks.fields.active'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge
          status={statusColors[status] || 'default'}
          text={status}
          icon={statusIcons[status]}
        />
      ),
    },
    {
      title: t('shared.webhooks.test_result.response_code'),
      dataIndex: 'response_status',
      key: 'response_status',
      render: (code) => (code ? <Tag color={code >= 200 && code < 300 ? 'green' : 'red'}>{code}</Tag> : '-'),
    },
    {
      title: t('shared.webhooks.test_result.duration'),
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: (ms) => (ms ? `${ms}ms` : '-'),
    },
    {
      title: t('shared.webhooks.fields.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!webhook) {
    return null;
  }

  const backPath = webhook.integration_id
    ? `/integrations/${webhook.integration_id}`
    : '/webhooks';

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['developer_tools', webhook.integration_id ? 'developer_tools.integrations' : 'developer_tools.webhooks', { label: webhook.name }]}
        actions={
          <Space>
            <Switch
              checked={webhook.active}
              onChange={handleToggleActive}
              checkedChildren={t('shared.webhooks.status.active')}
              unCheckedChildren={t('shared.webhooks.status.inactive')}
            />
            <Button
              icon={<SendOutlined />}
              loading={testLoading}
              onClick={handleTest}
            >
              {t('shared.webhooks.actions.test')}
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/webhooks/${id}/edit`, { state: { integrationId: webhook.integration_id } })}
            >
              {t('common.button.edit')}
            </Button>
            <Popconfirm
              title={t('shared.webhooks.confirm.delete')}
              onConfirm={handleDelete}
              okText={t('common.button.confirm')}
              cancelText={t('common.button.cancel')}
            >
              <Button danger icon={<DeleteOutlined />}>
                {t('common.button.delete')}
              </Button>
            </Popconfirm>
          </Space>
        }
      />

      {/* Basic Info */}
      <Card title={t('shared.webhooks.detail.basic_info')} style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t('shared.webhooks.fields.name')}>
            {webhook.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.webhooks.fields.active')}>
            <Badge
              status={webhook.active ? 'success' : 'default'}
              text={webhook.active ? t('shared.webhooks.status.active') : t('shared.webhooks.status.inactive')}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.webhooks.fields.url')} span={2}>
            <Text code copyable>{webhook.url}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.webhooks.fields.description')} span={2}>
            {webhook.description || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.webhooks.fields.created_at')}>
            {new Date(webhook.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.webhooks.fields.updated_at')}>
            {new Date(webhook.updated_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Signing Secret */}
      <Card title={t('shared.webhooks.detail.signature_info')} style={{ marginBottom: 16 }}>
        <Alert
          message={t('shared.webhooks.detail.signature_description')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Space>
          <Text code>
            {showSecret && fullSecret ? fullSecret : (webhook.secret_key || '********')}
          </Text>
          <Button
            size="small"
            icon={showSecret ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setShowSecret(!showSecret)}
          >
            {showSecret
              ? t('shared.webhooks.actions.hide_secret')
              : t('shared.webhooks.actions.show_secret')}
          </Button>
          {showSecret && fullSecret && (
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={handleCopySecret}
            >
              {t('shared.webhooks.actions.copy_secret')}
            </Button>
          )}
          <Popconfirm
            title={t('shared.webhooks.confirm.regenerate_secret')}
            onConfirm={handleRegenerateSecret}
            okText={t('common.button.confirm')}
            cancelText={t('common.button.cancel')}
          >
            <Button size="small" icon={<ReloadOutlined />}>
              {t('shared.webhooks.actions.regenerate_secret')}
            </Button>
          </Popconfirm>
        </Space>
      </Card>

      {/* Subscribed Events */}
      <Card title={t('shared.webhooks.detail.subscribed_events')} style={{ marginBottom: 16 }}>
        <Space wrap size={[8, 8]}>
          {(webhook.events || []).map((event) => (
            <Tag key={event} color="blue">
              {t(`shared.webhooks.events.${event}`, event)}
            </Tag>
          ))}
        </Space>
      </Card>

      {/* Recent Deliveries */}
      <Card
        title={t('shared.webhooks.detail.recent_deliveries')}
        extra={
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={fetchWebhook}
          >
            {t('common.button.refresh')}
          </Button>
        }
      >
        <Table
          dataSource={webhook.recent_deliveries || []}
          columns={deliveryColumns}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('shared.webhooks.detail.no_deliveries') }}
          size="small"
        />
      </Card>
    </div>
  );
}
